<template>
    <div>
        <common-step-progressbar
            :steps="progressbarSteps"
            :currentStep="progressbarCurrentStep"
            @go-to-step="goToStep"
        ></common-step-progressbar>

        <common-page-plain>
            <div v-if="!isCreatingCompleted" class="configure-offer-page__buttons">
                <common-buttons align="left" class="buttons__spacing--small">
                    <div>
                        <common-button
                            v-if="showBackButton"
                            :isSubmittable="false"
                            type="secondary"
                            size="medium-small"
                            :label="$t('common.microcopy.back')"
                            :isFluid="isFluid"
                            @click="goToPreviousStep"
                        ></common-button>
                    </div>
                </common-buttons>
            </div>
            <common-content-spacer height="medium">
                <common-page-header :heading="pageHeading" :content="pageContent" :isCenter="true"></common-page-header>

                <common-content-wrapper v-show="progressbarCurrentStep === 0" width="medium">
                    <create-company-form
                        :countryOptions="countryOptions"
                        @company-data-success="populateCompanyInfo"
                    ></create-company-form>
                </common-content-wrapper>

                <common-content-wrapper v-show="progressbarCurrentStep === 1" width="medium">
                    <package-selector ref="packageSelector" @package-chosen="packageChosen"></package-selector>
                </common-content-wrapper>
                <common-content-wrapper
                    v-if="isCreatingCompleted"
                    class="create-company__congrats--wrapper"
                    width="medium"
                    :newCompany="newCompany"
                >
                    <common-icon name="congrats" size="mega"></common-icon>
                    <div class="create-company__congrats--became-partner">
                        {{ $t('pages.company.create_company.became_a_partner') }}
                    </div>
                    <div class="create-company__congrats--partner-info-one">
                        {{ $t('pages.company.create_company.partner_info_one') }}
                    </div>
                    <div class="create-company__congrats--partner-info-two">
                        {{ $t('pages.company.create_company.partner_info_two') }}
                    </div>
                    <common-button
                        :class="'create-company__congrats--button'"
                        :isSubmittable="false"
                        type="primary"
                        size="medium-small"
                        :label="$t('pages.company.create_company.go_to_your_company')"
                        @click="goToCompany"
                    ></common-button>
                </common-content-wrapper>

                <common-content-wrapper
                    v-show="isProfessionalPlanChosen"
                    class="create-company__choose--subdomain-wrapper"
                    width="medium"
                >
                    <choose-subdomain-form @subdomain-set="populateData"></choose-subdomain-form>
                </common-content-wrapper>
            </common-content-spacer>
        </common-page-plain>
    </div>
</template>

<script>
    import CreateCompanyForm from 'components/forms/company/CreateCompanyForm'
    import ChooseSubdomainForm from 'components/forms/company/ChooseSubdomainForm'
    import WindowResizeMixin from 'mixins/WindowResizeMixin'
    import RoutesEnum from 'enums/routes.enum'
    import CompanyPackagesEnum from 'enums/company_packages.enum'
    import CompanyAPI from 'api/CompanyAPI'
    import PackageSelector from 'components/page-elements/package/PackageSelector'
    import CountryEnum from 'enums/country.enum'

    export default {
        mixins: [WindowResizeMixin],

        components: { CreateCompanyForm, ChooseSubdomainForm, PackageSelector },

        data() {
            return {
                progressbarCurrentStep: 0,
                isSubsciptionYearly: false,
                chosenPackage: null,
                companyInfo: null,
                showAddAvailabilityComponent: false,
                showExistingAvailabilityComponent: false,
                newCompany: null,
                countryOptions: [],
            }
        },

        computed: {
            pageHeading() {
                if (this.isCreatingCompleted) {
                    return this.$t('pages.company.create_company.congratulations')
                }
                if (this.progressbarCurrentStep === 0) {
                    return this.$t('pages.company.create_company.enter_company_details')
                }
                if (this.isChoosePackageStep) {
                    return this.$t('pages.company.create_company.package_step.title')
                }

                return this.$t('pages.company.create_company.create_company')
            },
            progressbarSteps() {
                return [
                    this.$t('pages.company.create_company.progressbar.create_step'),
                    this.$t('pages.company.create_company.progressbar.package_step'),
                    this.$t('pages.company.create_company.progressbar.finish_step'),
                ]
            },
            pageContent() {
                if (this.isChoosePackageStep) {
                    return this.$t('pages.company.create_company.package_step.description')
                }
                return undefined
            },
            isChoosePackageStep() {
                return this.progressbarCurrentStep === 1
            },
            isCompleteStep() {
                return this.progressbarCurrentStep === 2
            },

            createNewButtonLabel() {
                return this.progressbarCurrentStep > 1 && !this.showAddAvailabilityComponent
                    ? this.$t('pages.offer.configure_offer.buttons.create_new')
                    : this.$t('common.microcopy.back')
            },
            useExistingButtonLabel() {
                return this.progressbarCurrentStep > 1 && !this.showExistingAvailabilityComponent
                    ? this.$t('pages.offer.configure_offer.buttons.use_existing')
                    : this.$t('common.microcopy.back')
            },

            showBackButton() {
                return (
                    this.progressbarCurrentStep > 0 &&
                    !this.showAddAvailabilityComponent &&
                    !this.showExistingAvailabilityComponent
                )
            },

            isCreatingCompleted() {
                return (
                    (this.progressbarCurrentStep === 2 && this.chosenPackage === CompanyPackagesEnum.PACKAGE.BASIC) ||
                    this.progressbarCurrentStep === 3
                )
            },
            isProfessionalPlanChosen() {
                return this.progressbarCurrentStep === 2 && this.chosenPackage === CompanyPackagesEnum.PACKAGE.PROFESSIONAL
            },
            isFluid() {
                return this.isSmartphoneLarge ? true : false
            },
        },

        methods: {
            fetchCountry() {
                this.countryOptions = Object.entries(CountryEnum).map(([key, value]) => ({
                    value: value,
                    label: this.$t(`forms.company.create_company.fields.country.${key}`),
                }))
            },
            goToNextStep() {
                this.progressbarCurrentStep = this.progressbarCurrentStep + 1
            },
            goToPreviousStep() {
                this.progressbarCurrentStep = this.progressbarCurrentStep - 1
            },
            goToStep(step) {
                this.progressbarCurrentStep = step
                this.showAddAvailabilityComponent = false
            },

            packageChosen(payload) {
                this.chosenPackage = payload.chosenPackage
                this.companyInfo.yearlySubscription = payload.isSubsciptionYearly

                if (this.chosenPackage === CompanyPackagesEnum.PACKAGE.BASIC) {
                    this.submit(this.companyInfo)
                } else {
                    this.goToNextStep()
                }
            },

            populateCompanyInfo(data) {
                this.$refs.packageSelector.changeCountry(data.countryId)
                this.goToNextStep()
                this.companyInfo = {
                    name: data.name,
                    pib: data.pib,
                    address: data.address,
                    cityId: data.cityId,
                }
            },

            populateData(data) {
                this.companyInfo.subdomain = data.subdomain
                this.companyInfo.theme = data.theme?.toUpperCase()

                this.submit(this.companyInfo)
            },

            submit(data) {
                this.$startLoader()
                CompanyAPI.createCompany(data)
                    .then((res) => {
                        this.newCompany = res
                        this.$auth.reloadUser().then(() => {
                            this.goToNextStep()
                            this.$stopLoader()
                        })
                    })
                    .catch((err) => {
                        const invalidFields = err.error.invalidFields
                        const invalidField = invalidFields.find(
                            (field) =>
                                field.field === 'pib' ||
                                field.field === 'name' ||
                                field.field === 'address' ||
                                field.field === 'cityId',
                        )
                        if (invalidField) this.progressbarCurrentStep = 0
                        this.$stopLoader()
                    })
            },

            goToCompany() {
                this.$router.push({
                    name: RoutesEnum.COMPANY.MY_COMPANY,
                    params: { id: this.newCompany.company.id, tabType: 'profile' },
                    query: { tab: 'profile' },
                })
            },

            isProfileFinished() {
                if (!this.$auth.getUser().firstName || !this.$auth.getUser().firstName) {
                    this.$toast.error(this.$t('pages.company.my_companies.no_peronal_info'))
                    this.$router.push({ name: RoutesEnum.USER.ME, params: { tabType: 'profile' } })
                } else if (!this.$auth.getUser().mobile) {
                    this.$toast.error(this.$t('pages.company.my_companies.no_peronal_info'))
                    this.$router.push({
                        name: RoutesEnum.USER.ME,
                        params: { tabType: 'personalDetails' },
                        query: { tab: 'personalDetails' },
                    })
                }
            },
        },

        mounted() {
            this.isProfileFinished()
            this.fetchCountry()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .configure-offer-page {
        &__buttons {
            display: flex;
            justify-content: space-between;

            @include breakpoint(smartphone-large) {
                display: block;
            }
        }
    }

    .create-company {
        &__congrats {
            &--wrapper {
                text-align: center;
            }

            &--became-partner {
                margin: 25px 0 25px 0;
                color: $color--txt-base-3;
            }

            &--partner-info-one {
                color: $color--txt-base-3;
            }
            &--partner-info-two {
                color: $color--txt-base-3;
            }
            &--button {
                margin-top: 40px;
            }
        }

        &__choose {
            &--subdomain {
                &-wrapper {
                    max-width: unset;
                }
            }
        }
    }
</style>
